/**
 * Iter-411o · Runtime freshness for attendance
 * Iter-411s · Extended to full app boot + auth lifecycle
 * =============================================
 *
 * Prevents another Samantha Lacroix incident where the PWA opened
 * before a deploy kept running the pre-Iter-411n CheckInOutModal in
 * memory — even though the fix was already live on the server.
 *
 * Iter-411s also prevents a stale AuthCallback runtime from booting
 * after a PWA update (Irion Noiri incident: Google login succeeded on
 * the server but the pre-Iter-411s AuthCallback JS silently no-op'd
 * on `setAuth?.(...)` and stranded the user on the welcome screen).
 *
 * How it works:
 *   1. On first page load, we snapshot the backend `build_id` returned
 *      by /api/version into a MODULE-LEVEL variable (survives across
 *      component mounts, wiped only by a full page reload).
 *   2. Every time a Check-In / Check-Out modal opens (and every time
 *      the app resumes from background while a modal is open) we
 *      re-fetch /api/version. If the current build_id differs from the
 *      snapshot, we know the server has been redeployed since page load
 *      and the running JS is stale. We reload.
 *   3. Iter-411s: On app boot AND on AuthCallback mount, we also
 *      compare the persisted build_id (localStorage) against the live
 *      backend build_id. Mismatch → run a selective legacy auth key
 *      migration and (once) reload so the fresh JS boots the fresh
 *      auth runtime. NEVER wipes user preferences, drafts, tokens or
 *      anything unrelated to auth.
 *   4. If /api/version is temporarily unavailable, we DO NOT block —
 *      attendance and every emergency recovery path (GPS fallback,
 *      onsite "I'm onsite" request, event code) remain accessible.
 *
 * This is targeted to attendance + auth surfaces — no global auto-reload.
 */

const API = process.env.REACT_APP_BACKEND_URL;

let _initialBuildId = null;   // snapshot taken on first successful probe
let _initialProbeDone = false;

const RELOAD_MARKER = 'iEvntur.reloadAfterFreshness';

/**
 * Fetch the current backend build_id.
 * @returns {Promise<string|null>} build_id, or null if endpoint unavailable
 */
async function _fetchBuildId() {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3500);
    const res = await fetch(`${API}/api/version`, {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const data = await res.json();
    return (data && typeof data.build_id === 'string') ? data.build_id : null;
  } catch (_e) {
    return null;
  }
}

/**
 * Snapshot the current build id — called once on first attendance
 * modal render, or eagerly at app boot. Idempotent.
 */
export async function initFreshnessSnapshot() {
  if (_initialProbeDone) return _initialBuildId;
  const id = await _fetchBuildId();
  if (id) {
    _initialBuildId = id;
    _initialProbeDone = true;
  }
  return id;
}

/**
 * Preserve the intended attendance action across a reload so the
 * modal can re-open automatically once the fresh bundle is running.
 * @param {'checkin'|'checkout'} type
 * @param {string} bookingId
 */
export function stashPendingAttendanceAction(type, bookingId) {
  try {
    sessionStorage.setItem(
      'iEvntur.pendingAttendance',
      JSON.stringify({ type, bookingId, ts: Date.now() }),
    );
  } catch (_e) { /* private mode / storage disabled — non-fatal */ }
}

export function readAndClearPendingAttendanceAction() {
  try {
    const raw = sessionStorage.getItem('iEvntur.pendingAttendance');
    if (!raw) return null;
    sessionStorage.removeItem('iEvntur.pendingAttendance');
    const parsed = JSON.parse(raw);
    // 5-minute window — stale pending actions are discarded.
    if (!parsed || !parsed.ts || (Date.now() - parsed.ts) > 5 * 60_000) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

/**
 * Compare current server build to the snapshot and reload if stale.
 *
 * @param {{ type: 'checkin'|'checkout', bookingId: string }} pending
 *        The attendance action the user is trying to perform. Preserved
 *        across the reload via sessionStorage so the modal can reopen.
 * @returns {Promise<{stale: boolean, reason?: string}>}
 *        stale=true means we are about to reload (or would if
 *        supported). Caller should render a brief message and NOT
 *        proceed with attendance actions.
 */
export async function ensureFreshRuntime(pending) {
  // First call — take the snapshot and let attendance proceed.
  if (!_initialProbeDone) {
    await initFreshnessSnapshot();
    return { stale: false };
  }
  // Endpoint was unreachable at boot — never block attendance.
  if (!_initialBuildId) {
    return { stale: false, reason: 'no_snapshot' };
  }
  const current = await _fetchBuildId();
  if (!current) {
    // Server temporarily unavailable — never a hard blocker.
    return { stale: false, reason: 'endpoint_unavailable' };
  }
  if (current === _initialBuildId) {
    return { stale: false };
  }
  // STALE. Preserve intent and reload.
  if (pending && pending.type && pending.bookingId) {
    stashPendingAttendanceAction(pending.type, pending.bookingId);
  }
  try {
    sessionStorage.setItem(RELOAD_MARKER, '1');
  } catch (_e) { /* noop */ }
  // Guard against reload loops — if we just reloaded and are STILL
  // stale, don't spin.
  try {
    if (window.__iEvnturFreshnessReloaded) {
      return { stale: true, reason: 'reload_loop_guard' };
    }
    window.__iEvnturFreshnessReloaded = true;
  } catch (_e) { /* noop */ }
  // Give the caller a chance to show the toast, then reload.
  setTimeout(() => {
    try { window.location.reload(); } catch (_e) { /* noop */ }
  }, 350);
  return { stale: true, reason: 'build_id_mismatch' };
}

/**
 * True if the previous page load ended with a freshness-triggered
 * reload. Callers may use this to auto-reopen the attendance modal.
 */
export function wasFreshnessReloadJustPerformed() {
  try {
    const v = sessionStorage.getItem(RELOAD_MARKER);
    if (v) sessionStorage.removeItem(RELOAD_MARKER);
    return v === '1';
  } catch (_e) {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Iter-411s — App-boot freshness + selective legacy auth key migration.
// ─────────────────────────────────────────────────────────────────────

const PERSISTED_BUILD_KEY = 'iEvntur.persistedBuildId';
const BOOT_RELOADED_FLAG  = 'iEvntur.bootFreshnessReloaded';

/**
 * Legacy auth keys that are known to be incompatible with the current
 * auth runtime and MUST be purged on a build mismatch. This list is
 * intentionally narrow — user preferences, saved drafts, invite carts,
 * PWA install state etc. are NOT touched.
 *
 * If you add to this list, document the incompatibility and the build
 * that introduced it.
 */
const LEGACY_AUTH_KEYS_TO_PURGE = [
  // Legacy Emergent OAuth flow (pre-2026-08-05) parked the raw session
  // fragment here. New flow uses AuthCallback in-memory only.
  'emergent_session_id',
  // Pre-Iter-272 login-timestamp key. Iter-272 renamed it to
  // `_last_login_at`. Old value would confuse the JWT grace-window.
  'last_login_ts',
  // Pre-Iter-256 impersonation shape (nested JSON). Iter-256 replaced
  // it with the four flat keys already cleared in AuthContext.login.
  'impersonation_state',
  // Pre-Iter-411s partial-user snapshot key. Iter-411s writes to
  // `user` directly; leftover values here would race.
  'auth_partial_user',
  // Pre-Iter-411s intermediate token used by the broken AuthCallback
  // (setAuth?.() no-op). Never trust it across a build boundary.
  'social_auth_pending_token',
];

/**
 * Selectively purge legacy auth keys that are incompatible with a
 * newer build. Safe: never touches `token`, `user`, `_last_login_at`,
 * PWA state, saved drafts, invite carts, or unrelated app storage.
 *
 * @returns {number} count of keys actually purged
 */
export function purgeLegacyAuthKeys() {
  let purged = 0;
  for (const k of LEGACY_AUTH_KEYS_TO_PURGE) {
    try {
      if (localStorage.getItem(k) !== null) {
        localStorage.removeItem(k);
        purged += 1;
      }
      if (sessionStorage.getItem(k) !== null) {
        sessionStorage.removeItem(k);
        purged += 1;
      }
    } catch (_e) { /* private mode / storage disabled */ }
  }
  return purged;
}

/**
 * App-boot freshness check.
 *
 * Compares the backend build_id against the value we persisted on the
 * previous successful boot. If they differ, we:
 *   1. Run a selective legacy-auth-key migration (leaves token & user
 *      intact — those are compatible across builds).
 *   2. Update the persisted build_id.
 *   3. Reload ONCE (guarded against loops) so the fresh JS boots.
 *
 * Never blocks — if /api/version is unreachable, we return without
 * reloading and let the app continue with whatever runtime is loaded.
 *
 * Callable from:
 *   • App.js root effect (covers app boot & PWA cold-start after update)
 *   • AuthCallback runFlow (covers PWA users who opened directly to /auth/callback)
 *
 * @param {{scope?: string}} opts
 * @returns {Promise<{reloaded: boolean, buildId: string|null}>}
 */
export async function ensureFreshRuntimeAtBoot(opts = {}) {
  const scope = opts.scope || 'app-boot';
  const current = await _fetchBuildId();
  if (!current) {
    // Endpoint unreachable — do NOT block the app.
    return { reloaded: false, buildId: null };
  }
  // Also seed the attendance-freshness snapshot so we don't force a
  // second reload the first time the attendance modal opens.
  if (!_initialProbeDone) {
    _initialBuildId = current;
    _initialProbeDone = true;
  }
  let persisted = null;
  try { persisted = localStorage.getItem(PERSISTED_BUILD_KEY); } catch (_e) { /* noop */ }

  // First boot ever on this device → just persist and continue.
  if (!persisted) {
    try { localStorage.setItem(PERSISTED_BUILD_KEY, current); } catch (_e) { /* noop */ }
    return { reloaded: false, buildId: current };
  }
  if (persisted === current) {
    return { reloaded: false, buildId: current };
  }

  // Mismatch — the persisted build predates the server's current
  // build. Migrate legacy auth keys and reload once.
  const purged = purgeLegacyAuthKeys();
  try { localStorage.setItem(PERSISTED_BUILD_KEY, current); } catch (_e) { /* noop */ }

  // Reload loop guard — refuse to reload twice in the same session.
  let alreadyReloaded = false;
  try { alreadyReloaded = sessionStorage.getItem(BOOT_RELOADED_FLAG) === '1'; } catch (_e) { /* noop */ }
  if (alreadyReloaded) {
    return { reloaded: false, buildId: current, purged, guard: 'loop_prevented' };
  }
  try { sessionStorage.setItem(BOOT_RELOADED_FLAG, '1'); } catch (_e) { /* noop */ }

  try {
    if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
      // Give React a tick to unmount cleanly, then hard reload.
      setTimeout(() => {
        try { window.location.reload(); } catch (_e) { /* noop */ }
      }, 50);
    }
  } catch (_e) { /* noop */ }
  return { reloaded: true, buildId: current, purged, scope };
}
